/**
 * Middleware - iotU v2 authentication service
 * Docs: http://repo.shelly.cloud:6040/docs/#/users?id=authentication
 *
 * Login: api2.shelly.cloud/v2/users/auth/login
 * Refresh: <user instance>.shelly.cloud/v2/users/auth/refresh
 *
 */

import type {
	LoginResponse,
	LoginResult,
	LoginUserMetadata,
	MultiUnitLoginResult,
	RefreshTokenResponse,
	RefreshTokenResult,
} from 'src/types/responses/usersAuthResponse';
import { api, cloud, postReq } from '../http';

import type { UserData } from 'src/types/UserData';
import { extractUserTokenData } from '../auth_token';
import { isMultiUnitResult } from '../auth_v2';
import { normalizeEmail } from 'src/helpers/email_helpers';

/**
 * Login request (v2)
 * - Requests are made to API2 Load Balancer
 *
 * @param email
 * @param pass - hashed password
 * @param unitId - optional unit ID for multi-unit accounts
 * @param subId - optional sub-unit ID for shared homes
 * @returns {Promise<LoginResult>} - Returns user data and tokens, or available units for selection
 * @throws {Error} - Throws an error if the login fails
 */
export const loginRequest = async (
	email: string,
	pass: string,
	unitId?: string,
	subId?: string,
): Promise<LoginResult> => {
	email = String(email).toLowerCase();

	const body: Record<string, string | number> = {
		email,
		password: pass,
	};

	if (unitId) {
		body.unit = Number(unitId);
	}

	if (subId) {
		body.sub = Number(subId);
	}

	const response = await api.post('/v2/users/auth/login', body);

	if (isMultiUnitResult(response.data)) {
		return {
			isok: true,
			multipleUnits: true,
			availableUnits: response.data.availableUnits,
			accessToken: response.data.accessToken,
			refreshToken: response.data.refreshToken,
			...(response.data.availableSubUnits && {
				availableSubUnits: response.data.availableSubUnits,
			}),
			selectedUnitId: response.data.selectedUnitId,
			data: {
				...normalizeUsermetadata(response, email, pass),
			},
		} as MultiUnitLoginResult;
	}

	const result = normalizeUsermetadata(response, email, pass);
	return { isok: true, data: result as UserData };
};

/**
 * Refresh token request (v2)
 * - Requests are made to the user's instance API server
 *
 * @param {string} refreshToken - The refresh token to use for updating Access Token & Refresh Token
 * @returns {Promise<RefreshTokenResult>} - Returns isok and user data
 * @throws {Error} - Throws an error if the token refresh fails
 **/
export const refreshTokenRequest = async (refreshToken: string): Promise<RefreshTokenResult> => {
	const result = await cloud
		.post('/v2/users/auth/refresh', {
			refreshToken,
		})
		.then((response: RefreshTokenResponse) => normalizeUsermetadata(response));

	return { isok: true, data: result };
};

/**
 * Normalize userMetada data from login response
 * - Normalizes the login response to UserData format
 * - Extracts user_id, is_sub, user_api_url from the JWT token
 * @param {LoginResponse} response - The login response from the API
 * @returns {UserData} - The normalized login response for UserData
 * @throws {Error} - Throws an error if the token data is not valid
 */
export const normalizeUsermetadata = (
	response: LoginResponse,
	email?: string,
	passwordHash?: string, //@todo: remove password from the user data at next stage
): Partial<UserData> => {
	// Users with one Sub unit have only availableSubUnits, no accessToken
	if (!response.data.accessToken) {
		return undefined;
	}

	const tokenData = extractUserTokenData(response.data.accessToken);
	const userMetadata = response.data.userMetadata;

	const notifications_urls = prepareNotificationsUrls(userMetadata);
	const result = {
		...(email ? { email: normalizeEmail(email) } : {}),
		...(passwordHash ? { password: passwordHash } : {}),
		user_id: tokenData.user_id,
		is_sub: tokenData.is_sub,
		temporary: tokenData.temporary === true,
		lang: userMetadata.lang,
		premium: Number(userMetadata.premium),
		timezone: userMetadata.tz,
		refresh_token: response.data.refreshToken,
		token: response.data.accessToken,
		regts: Math.floor(new Date(userMetadata.regts).getTime() / 1000),
		...(userMetadata?.pin && {
			lock_pin: userMetadata.pin,
		}),
		tmpk_valid: userMetadata.tmpKeyExpiration ? userMetadata.tmpKeyExpiration : undefined,
		notifications_urls,
		notifications_data: userMetadata.notifications,
		user_api_url: tokenData.user_api_url,
		...(response.data.availableSubUnits && { available_sub_units: response.data.availableSubUnits }),
	};

	return result;
};
/**
 * Add a new unit (home) to the current account
 * - Requests are made to the user's instance API server
 * - Requires main account auth (not sub unit)
 *
 * @param name - Optional name for the new unit
 * @param lang - Optional language setting
 * @param timezone - Optional timezone setting
 * @returns Unit connection details for the newly created unit
 */
export const addUnit = async (name?: string, lang?: string, timezone?: string) => {
	const body = { name, lang, timezone };
	const response = await postReq('/user/add_unit', body, 10000, null, false, false, {
		'Content-Type': 'x-www-form-urlencoded',
	});

	return response.data;
};

/**
 * Prepare WS notifications URLs
 * @param {LoginUserMetadata} userMetadata - User metadata containing notifications configuration
 * @returns
 */
const prepareNotificationsUrls = (userMetadata: LoginUserMetadata): string[] => {
	const hosts = userMetadata?.notifications?.hosts;
	const domain = 'shelly.cloud';
	const port = userMetadata?.notifications?.port || 6113;
	const protocol = userMetadata?.notifications?.protocol || 'wss:';
	const pathname = userMetadata?.notifications?.pathname || '/shelly/wss/hk_sock';
	return hosts.map((host) => `${protocol}//${host}.${domain}:${port}${pathname}`);
};
